import { useEffect, useMemo, useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Text,
  Button,
  Banner,
  Spinner,
  InlineStack,
  BlockStack,
  Badge,
  Card,
  ProgressBar,
} from "@shopify/polaris";
import { XIcon, FolderIcon, PageIcon } from "@shopify/polaris-icons";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "hooks";
import { RootState } from "configureStore";
import {
  closeCartDrawer,
  fetchCartThunk,
  removeItemThunk,
  removeFolderThunk,
  clearCartThunk,
  checkoutThunk,
  setCheckoutStatus,
} from "./documentCart.slice";
import {
  DocumentCartItem,
  downloadOrderZip,
  CART_SIZE_LIMIT_LABEL,
  arrangeParentAndChildRows,
} from "./document-cart.service";
import "./document-cart.scss";

interface FolderGroup {
  categoryId: string;
  path: string;
  items: DocumentCartItem[];
}

/**
 * Place each row in the same drawer section as its parent (when that parent is
 * also in the cart), so arrangeParentAndChildRows can nest across folder/file splits.
 */
function resolveDisplaySourceCategoryId(
  item: DocumentCartItem,
  itemsByFileId: Map<string, DocumentCartItem[]>,
  visited: Set<string> = new Set()
): string | null {
  if (!item.parentFile || visited.has(item.id)) {
    return item.sourceCategoryId;
  }
  visited.add(item.id);

  const candidates = itemsByFileId.get(item.parentFile) || [];
  if (!candidates.length) {
    return item.sourceCategoryId;
  }

  const sameGroupParent =
    candidates.find(
      (parent) => parent.sourceCategoryId === item.sourceCategoryId
    ) || null;
  const parent = sameGroupParent || candidates[0];
  return resolveDisplaySourceCategoryId(parent, itemsByFileId, visited);
}

function splitCartItems(items: DocumentCartItem[]) {
  const itemsByFileId = new Map<string, DocumentCartItem[]>();
  for (const item of items) {
    const list = itemsByFileId.get(item.fileId) || [];
    list.push(item);
    itemsByFileId.set(item.fileId, list);
  }

  const files: DocumentCartItem[] = [];
  const folderMap = new Map<string, FolderGroup>();

  for (const item of items) {
    const groupId = resolveDisplaySourceCategoryId(item, itemsByFileId);
    if (!groupId) {
      files.push(item);
      continue;
    }

    const existing = folderMap.get(groupId);
    if (existing) {
      existing.items.push(item);
      continue;
    }

    const groupSeed =
      items.find((candidate) => candidate.sourceCategoryId === groupId) ||
      item;

    folderMap.set(groupId, {
      categoryId: groupId,
      path:
        groupSeed.sourceCategoryPath ||
        groupSeed.categoryPath ||
        groupId,
      items: [item],
    });
  }

  return {
    files,
    folders: Array.from(folderMap.values()),
  };
}

const CartDrawer: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const { drawerOpen, items, count, availableCount, loading, mutating, checkoutLoading, checkoutStatus, checkoutOrderId, checkoutOrderNumber, checkoutError, totalSizeBytes, maxSizeBytes } =
    useAppSelector((state: RootState) => state.documentCartReducer);
  const [expandedFolders, setExpandedFolders] = useState<
    Record<string, boolean>
  >({});
  const [zipDownloading, setZipDownloading] = useState(false);

  useEffect(() => {
    if (drawerOpen && companyId) {
      dispatch(fetchCartThunk(companyId));
    }
  }, [drawerOpen, companyId, dispatch]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch(closeCartDrawer());
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen, dispatch]);

  const { files, folders } = useMemo(() => splitCartItems(items), [items]);
  const unavailableCount = count - availableCount;

  const handleClose = useCallback(() => {
    dispatch(closeCartDrawer());
  }, [dispatch]);

  const handleRemove = useCallback(
    async (itemId: string) => {
      if (!companyId) return;
      try {
        await dispatch(removeItemThunk({ companyId, itemId })).unwrap();
        toast.success(t("document_cart_removed"));
      } catch {
        toast.error(t("document_cart_remove_failed"));
        dispatch(fetchCartThunk(companyId));
      }
    },
    [companyId, dispatch, t]
  );

  const handleRemoveFolder = useCallback(
    async (categoryId: string) => {
      if (!companyId) return;
      try {
        const result = await dispatch(
          removeFolderThunk({ companyId, categoryId })
        ).unwrap();
        toast.success(
          t("document_cart_folder_removed", { count: result.removed })
        );
      } catch {
        toast.error(t("document_cart_remove_failed"));
        dispatch(fetchCartThunk(companyId));
      }
    },
    [companyId, dispatch, t]
  );

  const handleClear = useCallback(async () => {
    if (
      !companyId ||
      !count ||
      checkoutLoading ||
      checkoutStatus === "processing"
    ) {
      return;
    }
    try {
      await dispatch(clearCartThunk(companyId)).unwrap();
      toast.success(t("document_cart_cleared"));
    } catch {
      toast.error(t("document_cart_clear_failed"));
      dispatch(fetchCartThunk(companyId));
    }
  }, [companyId, count, checkoutLoading, checkoutStatus, dispatch, t]);

  const handleCheckout = useCallback(async () => {
    if (!companyId || !availableCount || checkoutStatus === "processing") {
      return;
    }

    try {
      const result = await dispatch(checkoutThunk(companyId)).unwrap();
      if (result.skipped?.length) {
        toast.message(
          t("document_cart_partial_skipped", {
            count: result.skipped.length,
          })
        );
      }
      if (result.mode === "sync" && result.order?.id) {
        toast.success(t("document_cart_download_started"));
        setZipDownloading(true);
        try {
          const orderNumber = result.order.orderNumber;
          await downloadOrderZip(
            companyId,
            result.order.id,
            orderNumber ? `${orderNumber}.zip` : undefined
          );
        } catch {
          toast.error(t("document_cart_download_failed"));
        } finally {
          setZipDownloading(false);
        }
      } else {
        toast.message(t("document_cart_preparing_zip"));
      }
    } catch (code) {
      if (String(code).includes("NOTHING_TO_DOWNLOAD")) {
        toast.error(t("document_cart_nothing_to_download"));
      } else if (String(code).includes("ZIP_TOO_LARGE")) {
        toast.error(t("document_cart_too_large", { limit: CART_SIZE_LIMIT_LABEL }));
      } else {
        toast.error(t("document_cart_checkout_failed"));
      }
    }
  }, [companyId, availableCount, checkoutStatus, dispatch, t]);

  const handleBrowse = useCallback(() => {
    if (!companyId) return;
    dispatch(closeCartDrawer());
    navigate(`/app/${companyId}/documents`);
  }, [companyId, dispatch, navigate]);

  const toggleFolder = useCallback((categoryId: string) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  }, []);

  const renderFileRow = (item: DocumentCartItem, isChild = false, depth = 0) => (
    <li
      key={item.id}
      className={`document-cart-item ${
        !item.available ? "document-cart-item--unavailable" : ""
      } ${isChild ? "document-cart-item--child" : ""}`}
      style={depth > 0 ? { marginLeft: `${depth * 20}px` } : undefined}
    >
      <div className="document-cart-item__main">
        <Text as="span" variant="bodyMd" truncate>
          {item.title || item.fileId}
        </Text>
        {!item.available && (
          <Badge tone="warning">{t("document_cart_unavailable")}</Badge>
        )}
      </div>
      <Button
        variant="plain"
        tone="critical"
        disabled={mutating}
        onClick={() => handleRemove(item.id)}
      >
        {t("remove")}
      </Button>
    </li>
  );

  if (!drawerOpen) return null;

  return (
    <div className="document-cart-drawer-root" role="presentation">
      <div
        className="document-cart-drawer-overlay"
        onClick={handleClose}
        aria-hidden
      />
      <aside
        className="document-cart-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={t("document_cart")}
      >
        <div className="document-cart-drawer__header">
          <div>
            <Text as="h2" variant="headingMd">
              {t("document_cart")}
              {count > 0 ? ` (${count})` : ""}
            </Text>
            <Text as="p" tone="subdued" variant="bodySm">
              {t("document_cart_subtitle")}
            </Text>
          </div>
          <button
            type="button"
            className="document-cart-drawer__close"
            onClick={handleClose}
            aria-label={t("close")}
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="document-cart-drawer__toolbar">
          <Button
            tone="critical"
            variant="plain"
            disabled={
              !count ||
              mutating ||
              checkoutLoading ||
              checkoutStatus === "processing"
            }
            onClick={handleClear}
          >
            {t("document_cart_clear_all")}
          </Button>
        </div>

        <div className="document-cart-drawer__body">
          {loading && !items.length ? (
            <div className="document-cart-drawer__loading">
              <Spinner size="small" />
            </div>
          ) : !items.length && checkoutStatus !== "idle" ? (
            <div className="document-cart-drawer__processing" style={{ padding: "16px 0" }}>
              <BlockStack gap="400">
                {checkoutStatus === "processing" && (
                  <Card>
                    <BlockStack gap="300" align="center">
                      <Spinner size="large" />
                      <Text as="p" variant="bodyMd" fontWeight="semibold" alignment="center">
                        {t("document_cart_preparing_zip")}
                      </Text>
                      <Text as="p" tone="subdued" variant="bodySm" alignment="center">
                        {t("document_cart_preparing_zip_hint", { defaultValue: "We are packing your files into a ZIP. This may take a few moments. Please keep this drawer open." })}
                      </Text>
                    </BlockStack>
                  </Card>
                )}
                {checkoutStatus === "ready" && (
                  <Card>
                    <BlockStack gap="300" align="center">
                      <div style={{ color: "var(--p-color-text-success)", fontSize: "2.5rem", fontWeight: "bold", textAlign: "center" }}>✓</div>
                      <Text as="p" variant="bodyMd" fontWeight="semibold" alignment="center">
                        {t("document_cart_download_ready_title", { defaultValue: "ZIP Pack Ready!" })}
                      </Text>
                      <Text as="p" tone="subdued" variant="bodySm" alignment="center">
                        {t("document_cart_download_ready_desc", { defaultValue: "If your file does not download automatically, click the button below." })}
                      </Text>
                      <Button
                        variant="primary"
                        onClick={async () => {
                          if (!companyId || !checkoutOrderId) return;
                          setZipDownloading(true);
                          try {
                            const fileName = checkoutOrderNumber ? `${checkoutOrderNumber}.zip` : undefined;
                            await downloadOrderZip(companyId, checkoutOrderId, fileName);
                          } catch {
                            toast.error(t("document_cart_download_failed"));
                          } finally {
                            setZipDownloading(false);
                          }
                        }}
                        loading={zipDownloading}
                      >
                        {t("download")}
                      </Button>
                    </BlockStack>
                  </Card>
                )}
                {checkoutStatus === "failed" && (
                  <Card>
                    <BlockStack gap="300" align="center">
                      <Text as="p" variant="bodyMd" fontWeight="semibold" tone="critical" alignment="center">
                        {t("document_cart_download_failed")}
                      </Text>
                      {checkoutError && (
                        <Text as="p" tone="critical" variant="bodySm" alignment="center">
                          {checkoutError === "ZIP_TOO_LARGE"
                            ? t("document_cart_too_large", { limit: CART_SIZE_LIMIT_LABEL })
                            : checkoutError === "NOTHING_TO_DOWNLOAD"
                            ? t("document_cart_nothing_to_download")
                            : t("document_cart_checkout_failed")}
                        </Text>
                      )}
                      <Button
                        onClick={() => {
                          dispatch(setCheckoutStatus({ status: "idle" }));
                        }}
                      >
                        {t("back", { defaultValue: "Back" })}
                      </Button>
                    </BlockStack>
                  </Card>
                )}
              </BlockStack>
            </div>
          ) : !items.length ? (
            <div className="document-cart-drawer__empty">
              <BlockStack gap="300">
                <Text as="p" alignment="center" tone="subdued">
                  {t("document_cart_empty")}
                </Text>
                <InlineStack align="center">
                  <Button onClick={handleBrowse}>
                    {t("document_cart_browse")}
                  </Button>
                </InlineStack>
              </BlockStack>
            </div>
          ) : (
            <BlockStack gap="500">
              {unavailableCount > 0 && (
                <Banner tone="warning">
                  {t("document_cart_unavailable_banner", {
                    count: unavailableCount,
                  })}
                </Banner>
              )}

              {/* Section 1 — Files */}
              <section className="document-cart-section">
                <div className="document-cart-section__header">
                  <PageIcon className="w-4 h-4" />
                  <Text as="h3" variant="headingSm" fontWeight="semibold">
                    {t("document_cart_tab_files")}
                  </Text>
                  <span className="document-cart-section__count">
                    {files.length}
                  </span>
                </div>
                {files.length === 0 ? (
                  <Text as="p" tone="subdued" variant="bodySm">
                    {t("document_cart_no_individual_files")}
                  </Text>
                ) : (
                  <ul className="document-cart-group__list">
                    {arrangeParentAndChildRows(files).map(
                      ({ item, isChild, depth }) =>
                        renderFileRow(item, isChild, depth)
                    )}
                  </ul>
                )}
              </section>

              {/* Section 2 — Folders */}
              <section className="document-cart-section">
                <div className="document-cart-section__header">
                  <FolderIcon className="w-4 h-4" />
                  <Text as="h3" variant="headingSm" fontWeight="semibold">
                    {t("document_cart_tab_folders")}
                  </Text>
                  <span className="document-cart-section__count">
                    {folders.length}
                  </span>
                </div>
                {folders.length === 0 ? (
                  <Text as="p" tone="subdued" variant="bodySm">
                    {t("document_cart_no_folders")}
                  </Text>
                ) : (
                  <BlockStack gap="300">
                    {folders.map((folder) => {
                      const expanded = !!expandedFolders[folder.categoryId];
                      return (
                        <div
                          key={folder.categoryId}
                          className="document-cart-folder"
                        >
                          <div className="document-cart-folder__header">
                            <button
                              type="button"
                              className="document-cart-folder__toggle"
                              onClick={() => toggleFolder(folder.categoryId)}
                            >
                              <FolderIcon className="w-4 h-4" />
                              <span className="document-cart-folder__name">
                                {folder.path}
                              </span>
                              <Badge>
                                {t("document_cart_folder_file_count", {
                                  count: folder.items.length,
                                })}
                              </Badge>
                            </button>
                            <Button
                              variant="plain"
                              tone="critical"
                              disabled={mutating}
                              onClick={() =>
                                handleRemoveFolder(folder.categoryId)
                              }
                            >
                              {t("remove")}
                            </Button>
                          </div>
                          {expanded && (
                            <ul className="document-cart-group__list document-cart-folder__files">
                              {arrangeParentAndChildRows(folder.items).map(
                                ({ item, isChild, depth }) =>
                                  renderFileRow(item, isChild, depth)
                              )}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </BlockStack>
                )}
              </section>
            </BlockStack>
          )}
        </div>

        <div className="document-cart-drawer__footer">
          {checkoutStatus === "processing" && (
            <Banner tone="info">{t("document_cart_preparing_zip")}</Banner>
          )}
          {count > 0 && (() => {
            const selectedSizeMB = (totalSizeBytes / (1024 * 1024)).toFixed(2);
            const limitSizeMB = maxSizeBytes / (1024 * 1024);
            const limitLabel = `${Number.isInteger(limitSizeMB) ? limitSizeMB : limitSizeMB.toFixed(2)} MB`;
            const progressPercent = maxSizeBytes > 0
              ? Math.min((totalSizeBytes / maxSizeBytes) * 100, 100)
              : 100;
            const tone = progressPercent > 90 ? "critical" : progressPercent > 70 ? "highlight" : "primary";
            
            return (
              <BlockStack gap="150">
                <InlineStack align="space-between">
                  <Text as="span" variant="bodySm" tone="subdued">
                    {t("document_cart_ready_summary", {
                      ready: availableCount,
                      total: count,
                    })}
                  </Text>
                  <Text as="span" variant="bodySm" fontWeight="semibold" tone={tone === "critical" ? "critical" : "subdued"}>
                    {`${selectedSizeMB} MB / ${limitLabel}`}
                  </Text>
                </InlineStack>
                <ProgressBar progress={progressPercent} size="small" tone={tone} />
              </BlockStack>
            );
          })()}
          <Button
            variant="primary"
            fullWidth
            disabled={!availableCount || checkoutLoading || checkoutStatus === "processing" || zipDownloading}
            loading={checkoutLoading || zipDownloading}
            onClick={handleCheckout}
          >
            {zipDownloading
              ? t("document_cart_downloading")
              : t("document_cart_download_all")}
          </Button>
        </div>
      </aside>
    </div>
  );
};

export default CartDrawer;
